import moment from "moment";
import { ESolarServiceProvider } from "../../../shared/oversight-core/enums/solar-service-provider";
import { IGoodWeSolarPlantAlert } from "../../../shared/oversight-core/interfaces/good-we-solar-plant-alert";
import { IGroWattSolarPlantAlert } from "../../../shared/oversight-core/interfaces/gro-watt-solar-plant-alert";
import { INormalizedAlert } from "../../../shared/oversight-core/interfaces/normalized-alert";
import { IPowerHubSolarPlantAlert } from "../../../shared/oversight-core/interfaces/powerhub-solar-plant-alert";
import { IScoutSolarPlantAlert } from "../../../shared/oversight-core/interfaces/scout-solar-plant-alert";
import { ISolarPlantAlerts } from "../../../shared/oversight-core/interfaces/solar-plant-alerts";
import {
  formatEpochToDate,
  getTimestampDuration,
} from "../../../shared/oversight-core/utils/time-utils";
import { severityLabels } from "../components/alert-filter-fields/alert-filter-fields";

export interface ISitesErrorLog {
  plantName: string;
  plantTimeZone: string;
  plantIdentity: string;
  generationServiceProviderId: string;
  vrmAlert: ISolarPlantAlerts;
  goodWeAlert: IGoodWeSolarPlantAlert;
  groWattAlert: IGroWattSolarPlantAlert;
  powerHubAlert: IPowerHubSolarPlantAlert;
  scoutAlert: IScoutSolarPlantAlert;
  // The same alert read into the fields every brand shares; absent until the next sync after the
  // backend started providing it.
  normalized?: INormalizedAlert;
}

const UTC_DATE_TIME_FORMAT = "YYYY/MM/DD HH:mm:ss";

const formatUtcDateTime = (value: string | number) =>
  moment.utc(value).format(UTC_DATE_TIME_FORMAT);

// happentime is when the alert began; recoverytime is when it ended, not when it started.
const getGoodWeStartTime = (alert: IGoodWeSolarPlantAlert): string =>
  formatUtcDateTime(alert.happentime);

export const getAlertStartTime = (
  item: ISitesErrorLog,
  serviceProvider?: ESolarServiceProvider,
): string => {
  switch (serviceProvider) {
    case ESolarServiceProvider.VRM:
      return formatEpochToDate(item.vrmAlert.started, item.plantTimeZone);
    case ESolarServiceProvider.GOODWE:
      return getGoodWeStartTime(item.goodWeAlert);
    case ESolarServiceProvider.GROWATT:
      return formatUtcDateTime(item.groWattAlert.startTime);
    case ESolarServiceProvider.POWERHUB:
      return formatUtcDateTime(item.powerHubAlert.startTime);
    case ESolarServiceProvider.SCOUT:
      if (!item.scoutAlert.startTime) {
        return "";
      }
      return formatUtcDateTime(item.scoutAlert.startTime);
    default:
      return item.normalized?.startTime
        ? formatUtcDateTime(item.normalized.startTime)
        : "";
  }
};

export const getAlertDescription = (
  item: ISitesErrorLog,
  serviceProvider?: ESolarServiceProvider,
): string => {
  switch (serviceProvider) {
    case ESolarServiceProvider.VRM:
      return item.vrmAlert.description ?? "-";
    case ESolarServiceProvider.GOODWE:
      return item.goodWeAlert.warningname ?? "-";
    case ESolarServiceProvider.GROWATT:
      return item.groWattAlert.alarmMessage ?? "-";
    case ESolarServiceProvider.POWERHUB:
      return item.powerHubAlert.description ?? "-";
    case ESolarServiceProvider.SCOUT:
      return item.scoutAlert.description ?? item.scoutAlert.alertName ?? "-";
    default:
      return item.normalized?.name ?? "-";
  }
};

export const getAlertSeverityLabel = (item: ISitesErrorLog): string =>
  item.normalized?.severity ? severityLabels[item.normalized.severity] : "-";

const getVrmStatusLabel = (alert: ISolarPlantAlerts): string => {
  if (!alert.cleared) {
    return "Active";
  }

  if (alert.started && alert.cleared) {
    return getTimestampDuration(alert.started, alert.cleared);
  }

  return "-";
};

export const getAlertStatusLabel = (
  item: ISitesErrorLog,
  serviceProvider?: ESolarServiceProvider,
): string => {
  // The backend's reading of open/closed covers every brand the same way (GoodWe used to fall
  // through to "Active" whatever its state).
  if (item.normalized) {
    if (item.normalized.open) {
      return "Active";
    }

    return serviceProvider === ESolarServiceProvider.VRM
      ? getVrmStatusLabel(item.vrmAlert)
      : "Cleared";
  }

  switch (serviceProvider) {
    case ESolarServiceProvider.VRM:
      return getVrmStatusLabel(item.vrmAlert);
    case ESolarServiceProvider.GROWATT:
      if (item.groWattAlert.status === 1) {
        return "Cleared";
      }
      return "Active";
    case ESolarServiceProvider.POWERHUB:
      if (item.powerHubAlert.isActive) {
        return "Active";
      }
      return "Cleared";
    case ESolarServiceProvider.SCOUT:
      if (
        item.scoutAlert.endTime ||
        item.scoutAlert.status?.toUpperCase() === "CLEARED"
      ) {
        return "Cleared";
      }
      return "Active";
    default:
      return "Active";
  }
};

export const getProviderLogoSize = (
  serviceProvider?: ESolarServiceProvider,
): { width: number; height: number } => {
  if (serviceProvider === ESolarServiceProvider.GOODWE) {
    return { width: 20, height: 20 };
  }

  return { width: 16, height: 15 };
};
